// src/oura/components/fixes/model.ts
//
// What the Fixes screen derives from the plan: who moves next on each task,
// which channels it moves, when it is due or how late it is, what closed it,
// and which recommendations the plan covers. Pure functions, shared by the
// grouped list, the week plan and the fix drawer so the three cannot disagree.

import type { AeoActivityEntry, AeoTaskImpact } from "@service/aeo/types";
import type { AuditIssue, Recommendation } from "../../audit";
import type { TaskChannel, TaskStatus, TrackerBlock, TrackerTask, TrackerWeek } from "../../tracker";
import { STAFF_STATUS, type StaffStatus, type StatusStyle } from "@oura/ui";

/** A task as the screen holds it: the plan's task, in the block it sits in now. */
export type PlacedTask = TrackerTask & { block: TrackerBlock };

/* ------------------------------------------------------------------------- *
 * Status words (D8)
 * ------------------------------------------------------------------------- */

/**
 * The task's own status, in the staff words. `blocked` keeps its own word:
 * it is a status the data sets, and "Needs the client" is a grouping (see
 * `whoMovesNext`), not something a task can be set to.
 */
export const TASK_STATUS: Record<TaskStatus, StatusStyle> = {
    not_started: STAFF_STATUS.not_started,
    in_progress: STAFF_STATUS.in_progress,
    blocked: { label: "Blocked", tone: "amber" },
    done: STAFF_STATUS.done,
};

/** The statuses staff set by hand; blocked comes from the data. */
export const SETTABLE: TaskStatus[] = ["not_started", "in_progress", "done"];

/**
 * The block the plan is in now: the first with work still open, which is also
 * the block the week plan opens on. Not `tracker.currentWeek`: the backend
 * sets that to Kickoff when it builds the plan and to Week 4 on a re-scan, and
 * moves it nowhere in between.
 */
export function currentBlockOf(weeks: ReadonlyArray<Pick<TrackerWeek, "week" | "tasks">>): TrackerBlock {
    const open = weeks.find((w) => w.tasks.some((t) => t.status !== "done"));
    return (open ?? weeks[weeks.length - 1])?.week ?? 4;
}

/** What `whoMovesNext` needs beyond the task itself. */
export interface MovesNextContext {
    /** Task ids a published ask is waiting on the client for. */
    waitingOnClient: ReadonlySet<string>;
    /** Today as `YYYY-MM-DD` (`todayKey`). */
    today: string;
    /** The block the plan is in now (`currentBlockOf`). */
    currentBlock: TrackerBlock;
}

/**
 * Who moves next on a task: the grouping of the default Fixes view, and of
 * Overview's "What to do next" (L6).
 *
 * Needs the client when a published ask is waiting on them for the task, or
 * when the client owns it (the three touchpoints) and it is live: started,
 * late, or in the block the plan is in now. A touchpoint in a later block that
 * nobody has asked about is not the client's move yet, so it waits with the
 * rest of the work that has not started. A blocked task of ours with no ask
 * stays with us: it is started work somebody here has to unblock.
 */
export function whoMovesNext(task: PlacedTask, ctx: MovesNextContext): StaffStatus {
    if (task.status === "done") return "done";
    if (ctx.waitingOnClient.has(task.id)) return "needs_client";
    if (task.owner === "client") {
        const live = task.status !== "not_started" || daysLate(task, ctx.today) > 0 || task.block === ctx.currentBlock;
        return live ? "needs_client" : "not_started";
    }
    return task.status === "not_started" ? "not_started" : "in_progress";
}

/** A group of the default Fixes view: the proposals, a status, and the recommendations with no step. */
export type FixesGroup = "proposed" | StaffStatus | "not_in_plan";

/**
 * The GroupFold id of one group, per business: a group folded or opened by
 * hand on one client stays that way for that client only. The deep links
 * into Fixes open a group by this id.
 */
export function fixesGroupId(businessId: string, group: FixesGroup): string {
    return `fixes.${businessId}.${group}`;
}

export const OWNER_LABEL: Record<TrackerTask["owner"], string> = { ourteam: "ourteam", client: "Client" };

/* ------------------------------------------------------------------------- *
 * Channels
 * ------------------------------------------------------------------------- */

const CHANNEL_LABEL: Record<TaskChannel, string> = { ai: "AI assistants", google: "Google search" };

/**
 * The backend's own rule (`_HELPS` in aeo_tracker.py), used until it
 * serializes `helps` (handoff "Serialize task.helps"). A check is read by an
 * assistant and a crawler alike, so it moves both; a manual step moves
 * neither by itself, which is an answer rather than a gap.
 */
const HELPS_BY_SOURCE: Record<string, TaskChannel[]> = {
    query: ["ai"],
    search: ["google"],
    check: ["ai", "google"],
    manual: [],
};

/** "AI assistants", "Google search": what the work moves, in plan order. */
export function movesOf(task: TrackerTask): string[] {
    const helps = task.helps ?? HELPS_BY_SOURCE[task.source?.type ?? "manual"] ?? [];
    return helps.map((c) => CHANNEL_LABEL[c]);
}

/* ------------------------------------------------------------------------- *
 * Dates
 * ------------------------------------------------------------------------- */

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * A calendar day at local midnight, from `YYYY-MM-DD` or a full ISO stamp.
 *
 * A date-only string is a day already and is read as written. A stamp is a
 * moment (`closedBy.at` is UTC), so its day is the one the viewer was in when
 * it happened: a close at 02:00 in Kuala Lumpur is on the 18th here, even
 * though its UTC date is the 17th.
 */
function calendarDay(value: string): Date | null {
    if (value.length > 10) {
        const at = new Date(value);
        return Number.isNaN(at.getTime()) ? null : new Date(at.getFullYear(), at.getMonth(), at.getDate());
    }
    const [y, m, d] = value.split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
}

/** Today as `YYYY-MM-DD` in the viewer's calendar: the form due dates compare in. */
export function todayKey(now: Date = new Date()): string {
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

/** "Fri 2 Oct". */
export function dayDate(value: string): string {
    const d = calendarDay(value);
    return d ? `${WEEKDAY[d.getDay()]} ${d.getDate()} ${MONTH[d.getMonth()]}` : "";
}

/** "18 Sep". */
function shortDay(value: string): string {
    const d = calendarDay(value);
    return d ? `${d.getDate()} ${MONTH[d.getMonth()]}` : "";
}

/**
 * Whole days a task is past its due date, 0 when it is not.
 *
 * `targetDate` is always a real date on a live plan (the block's milestone
 * when nobody set one, ourteam-backend#4508), so a date passing is the plan's
 * own claim, not one made here. Done work is never late.
 */
export function daysLate(task: TrackerTask, today: string): number {
    if (task.status === "done" || !task.targetDate) return 0;
    const due = calendarDay(task.targetDate);
    const now = calendarDay(today);
    if (!due || !now) return 0;
    return Math.max(0, Math.round((now.getTime() - due.getTime()) / 86_400_000));
}

export function lateLabel(days: number): string {
    return `${days} ${days === 1 ? "day" : "days"} late`;
}

export function dueLabel(task: TrackerTask): string | null {
    return task.targetDate ? `Due ${dayDate(task.targetDate)}` : null;
}

/**
 * "Done 18 Sep", or "Done" with no date.
 *
 * A person ticking a task done records no date on live today (handoff "Done
 * date on tasks marked done by hand"), so only a platform close carries one.
 * A missing date is left missing rather than guessed from anything nearby.
 */
export function doneLabel(task: TrackerTask): string {
    const at = task.doneAt ?? task.closedBy?.at;
    const day = at ? shortDay(at) : "";
    return day ? `Done ${day}` : "Done";
}

/* ------------------------------------------------------------------------- *
 * What a task is for, and what it did
 * ------------------------------------------------------------------------- */

function pagesWord(n: number): string {
    return `${n} ${n === 1 ? "page" : "pages"}`;
}

/**
 * The one line under a fix's title: why it is on the plan.
 *
 * In order of how much it says: the backend's own cause; the finding it
 * closes with its live page count; the question it answers; the task's note.
 */
export function whyLine(task: TrackerTask, issue?: AuditIssue): string | null {
    if (task.resolved) return "Nothing left to fix. The last scan found no pages with this problem.";
    if (task.cause) return task.cause;
    const source = task.source;
    if (source?.type === "check" && issue) {
        return `${issue.label}: ${pagesWord(task.affectedPages?.length ?? issue.count)}`;
    }
    if (source?.type === "query" && source.ref) {
        const rest = (task.sourceQueries?.length ?? 1) - 1;
        return `Not named for "${source.ref}"` + (rest > 0 ? ` and ${rest} more ${rest === 1 ? "question" : "questions"}` : "");
    }
    if (source?.type === "search" && source.ref) {
        return `Google shows the site for "${source.ref}", and no page answers it`;
    }
    return task.notes || null;
}

/**
 * What the work itself is, for a row or the drawer: the finding's fix steps,
 * the brief for the questions a page must answer, or the task's note.
 */
export function taskNoteText(task: TrackerTask, issue?: AuditIssue): string {
    const steps = task.source?.type === "check" ? issue?.fix ?? [] : [];
    const queries = task.sourceQueries ?? [];
    if (steps.length > 0) {
        // Each step is already a complete instruction ending in a full stop, so
        // they join into a paragraph rather than needing glue words.
        return steps.map((x) => x.trim().replace(/\.$/, "")).join(". ") + ".";
    }
    if (queries.length > 0) {
        const shown = queries.slice(0, 3).map((q) => `"${q}"`);
        const rest = queries.length - shown.length;
        return `Write the page that answers ${shown.join(", ")}`
            + (rest > 0 ? ` and ${rest} more question${rest === 1 ? "" : "s"}` : "")
            + ".";
    }
    return task.notes ?? "";
}

/** A task the crawl says has nothing left to do (see Tracker.tsx). */
export function isResolvedByCrawl(task: TrackerTask): boolean {
    return task.source?.type === "check"
        && Array.isArray(task.affectedPages)
        && task.affectedPages.length === 0;
}

export interface ImpactLine {
    text: string;
    at: string | null;
}

/**
 * The reasons the auto-task flip writes (`flip_auto_tasks_done` in
 * aeo_tracker.py). They say a run finished, not what the work changed.
 */
const RUN_FINISHED = /^(The scan that produced|A platform run completed)/;

/** Why a re-measure could not be compared, said with no number from either side. */
const NOT_COMPARABLE: Record<NonNullable<AeoTaskImpact["reason"]>, string> = {
    method_changed: "Not comparable: the scan measured differently.",
    questions_changed: "Not comparable: the questions this covers changed after the work was done.",
    crawl_blocked: "Not comparable: the re-crawl could not read enough of the site.",
    crawl_failed: "Not comparable: the re-crawl did not run.",
    check_not_measured: "Not comparable: the scan did not measure this.",
    withheld_by_google: "Not comparable: Google no longer reports this search.",
};

/**
 * What the platform recorded about the work's effect, or null.
 *
 * Only ever a sentence the backend wrote, on the same measure the task was
 * raised on: the impact check once it ships, then the reason a re-measure
 * gave for closing the task, then an entry Oura itself logged against the
 * task or its check (a check back to zero, a search that moved). A person's
 * own note is a delivery record, not an impact, and is left out. Nothing is
 * computed here, so nothing here can cross a trend break.
 *
 * A reading across a change of method is stated as that, in a fixed line,
 * so no before or after figure is ever shown for it.
 *
 * `closedBy` counts only while the task is done: a rebuild carries it forward
 * onto a task somebody has since reopened. And a run merely finishing is not
 * an impact, so the auto-task flip's reasons are left out too.
 */
export function impactOf(task: TrackerTask, activity: AeoActivityEntry[] | null): ImpactLine | null {
    const impact = task.impact;
    if (impact?.outcome === "not_comparable") {
        return { text: impact.reason ? NOT_COMPARABLE[impact.reason] : "Not comparable: the measure changed.", at: impact.checkedAt };
    }
    if (impact?.sentence) return { text: impact.sentence, at: impact.checkedAt };
    const closed = task.status === "done" ? task.closedBy : undefined;
    if (closed?.reason && !RUN_FINISHED.test(closed.reason)) return { text: closed.reason, at: closed.at };
    const checkKey = task.source?.type === "check" ? task.source.ref : null;
    const entry = (activity ?? []).find((e) =>
        e.author.kind === "oura"
        && (e.taskId === task.id || (checkKey !== null && e.evidence?.check === checkKey)),
    );
    return entry ? { text: entry.body, at: entry.at } : null;
}

/* ------------------------------------------------------------------------- *
 * Recommendations
 * ------------------------------------------------------------------------- */

/**
 * Resolve a Recommendation card to the tracker task it belongs to.
 *
 * There IS an id join, and it was being thrown away. The BE builds one
 * recommendation per failing audit check with `id = "rec_<check_key>"`
 * (`_recommendations()` in app/dependencies/aeo_overview.py: "stable id = check
 * key so a weakness maps to its fix"), and it stamps the tracker tasks that
 * resolve a check with `source: {type: "check", ref: "<check_key>"}`
 * (`_TEMPLATE_CHECK_SOURCE` in aeo_tracker.py). The two sides already agree on a
 * key; nothing was reading it.
 *
 * What used to happen instead was a word-overlap match on the card's TITLE, and
 * it was measurably wrong. Run against the real live titles (the check's first
 * fix step) and the real generated plan, of the seven recommendations that have
 * or could have a task it landed on the right one 5 times, on the WRONG task
 * once (`missing_meta` → "Fix titles & meta", a task that exists to close
 * `dup_titles`), and nowhere at all once, and that once was `broken_4xx`, the
 * only error-severity check there is, i.e. the top card in the High column.
 * "Restore the page if it was removed by mistake." and "Fix broken pages …
 * Redirect or restore every URL …" share exactly one significant word.
 *
 * So: key first, text only as a fallback for fixture businesses (whose ids are
 * `rec-schema`-style and carry no check key).
 */
export function checkKeyOfRec(recId: string | null): string | null {
    // `rec_<check_key>` is the live shape. Fixture ids use a hyphen and are not
    // check keys at all, so they must not be mistaken for one.
    return recId && recId.startsWith("rec_") ? recId.slice(4) : null;
}

export function resolveTaskByCheck<T extends TrackerTask>(tasks: T[], checkKey: string): T | undefined {
    return tasks.find((t) => t.source?.type === "check" && t.source.ref === checkKey);
}

const HINT_STOPWORDS = new Set([
    "the", "and", "for", "with", "that", "this", "your", "from", "into", "onto",
    "all", "every", "existing", "add", "fix", "new",
]);
function hintTokens(s: string): string[] {
    return s
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter((w) => w.length >= 3 && !HINT_STOPWORDS.has(w));
}
export function resolveTaskByHint<T extends TrackerTask>(tasks: T[], hint: string): T | undefined {
    const wanted = new Set(hintTokens(hint));
    if (wanted.size === 0) return undefined;
    let best: T | undefined;
    let bestScore = 0;
    for (const t of tasks) {
        // Deduped via `seen` so a repeated word scores once (ES5 target: a
        // Set cannot be for-of iterated here).
        const words = hintTokens(`${t.action} ${t.notes ?? ""}`);
        const seen = new Set<string>();
        let score = 0;
        for (const w of words) {
            if (!seen.has(w) && wanted.has(w)) score += 1;
            seen.add(w);
        }
        if (score > bestScore) {
            bestScore = score;
            best = t;
        }
    }
    return bestScore >= 2 ? best : undefined;
}

export interface RecommendationJoin {
    /** The recommendation a task answers, by task id: it lends the row its
     *  priority and the drawer its impact sentence. */
    byTask: Map<string, Recommendation>;
    /** Recommendations no task in the plan covers ("Not in the plan"). */
    unplanned: Recommendation[];
}

/**
 * Fold the Overview's recommendations into the plan (D7): a recommendation
 * joined to a task IS that task's row, and one with no task is listed as not
 * in the plan.
 *
 * Joined here against the plan on screen rather than read from `workState`:
 * the tracker is re-fetched on every Fixes visit and replaced by a rebuild,
 * while `workState` was computed when the business detail loaded.
 */
export function joinRecommendations(recs: Recommendation[], tasks: TrackerTask[]): RecommendationJoin {
    const byTask = new Map<string, Recommendation>();
    const unplanned: Recommendation[] = [];
    for (const rec of recs) {
        const key = checkKeyOfRec(rec.id);
        const task = key ? resolveTaskByCheck(tasks, key) : resolveTaskByHint(tasks, rec.title);
        if (!task) unplanned.push(rec);
        // Recommendations arrive most severe first, so the first to claim a
        // task is the one it answers.
        else if (!byTask.has(task.id)) byTask.set(task.id, rec);
    }
    return { byTask, unplanned };
}

/** High first. The order recommendations are read in wherever they are listed. */
export const PRIORITY_RANK: Record<Recommendation["priority"], number> = { high: 0, medium: 1, low: 2 };

/**
 * Order within a group: late first (most late first), then the priority the
 * joined recommendation gives, then plan order. The sort is stable, so ties
 * keep the plan's own sequence.
 */
export function byUrgency(recByTask: Map<string, Recommendation>, today: string) {
    const rank = (t: PlacedTask) => {
        const rec = recByTask.get(t.id);
        return rec ? PRIORITY_RANK[rec.priority] : 3;
    };
    return (a: PlacedTask, b: PlacedTask): number =>
        daysLate(b, today) - daysLate(a, today) || rank(a) - rank(b) || a.block - b.block;
}
